import freeTheBunnyWorkers from "../../../assets/json/4.1-free-the-bunny-workers.json"
import {Player} from "@lottiefiles/react-lottie-player";
import {getRandomColor} from "../../../utils/GetRandomColor";
import IconText from "../../IconText";
import {faKey} from "@fortawesome/free-solid-svg-icons";
export default function FreeTheBunnyWorkers() {
    const lottieStyle = {
        border: `5px solid ${getRandomColor()}`,
    };
    
    return (
        <div className={"blog-body"}>
            <h1><IconText text={"4.1. Free the Bunny Workers"} icon={faKey}/></h1>

            <h2>Question</h2>
            <div className={"blog-quote"}>
                <p>
                    You need to free the bunny workers before Commander Lambda's space station explodes! Unfortunately,
                    the
                    Commander was very careful with the highest-value workers -- they all work in separate,
                    maximum-security
                    work rooms. The rooms are opened by putting keys into each console, then pressing the open button on
                    each console simultaneously. When the open button is pressed, each key opens its corresponding lock
                    on
                    the work room. So, the union of the keys in all of the consoles must be all of the keys. The scheme
                    may
                    require multiple copies of one key given to different minions.
                </p>
                <p>
                    The consoles are far enough apart that a separate minion is needed for each one. Fortunately, you
                    have
                    already relieved some bunnies to aid you - and even better, you were able to steal the keys while
                    you
                    were working as Commander Lambda's assistant. The problem is, you don't know which keys to use at
                    which
                    consoles. The consoles are programmed to know which keys each minion had, to prevent someone from
                    just
                    stealing all of the keys and using them blindly. There are signs by the consoles saying how many
                    minions
                    had some keys for the set of consoles. You suspect that Commander Lambda has a systematic way to
                    decide
                    which keys to give to each minion such that they could use the consoles.
                </p>
                <p>
                    You need to figure out the scheme that Commander Lambda used to distribute the keys. You know how
                    many
                    minions had keys, and how many consoles are by each work room. You know that Command Lambda wouldn't
                    issue more keys than necessary (beyond what the key distribution scheme requires), and that you need
                    as
                    many bunnies with keys as there are consoles to open the work room.
                </p>
                <p>
                    Given the number of bunnies available and the number of locks required to open a work room, write a
                    function solution(num_buns, num_required) which returns a specification of how to distribute the
                    keys
                    such that any num_required bunnies can open the locks, but no group of (num_required - 1) bunnies
                    can.
                </p>
                <p>
                    Each lock is numbered starting from 0. The keys are numbered the same as the lock they open (so for
                    a
                    duplicate key, the number will repeat, since it opens the same lock). For a given bunny, the keys
                    they
                    get is represented as a sorted list of the numbers for the keys. To cover all of the bunnies, the
                    final
                    solution is represented by a sorted list of each individual bunny's list of keys. Find the
                    lexicographically least such key distribution - that is, the first bunny should have keys
                    sequentially
                    starting from 0.
                </p>
                <p>
                    num_buns will always be between 1 and 9, and num_required will always be between 0 and 9 (both
                    inclusive). For example, if you had 3 bunnies and required only 1 of them to open the cell, you
                    would
                    give each bunny the same key such that any of the 3 of them would be able to open it, like so: <br/>
                    [ <br/>
                    [0], <br/>
                    [0], <br/>
                    [0], <br/>
                    ] <br/>
                    If you had 2 bunnies and required both of them to open the cell, they would receive different keys
                    (otherwise they wouldn't both actually be required), and your solution would be as follows: <br/>
                    [ <br/>
                    [0], <br/>
                    [1], <br/>
                    ] <br/>
                    Finally, if you had 3 bunnies and required 2 of them to open the cell, then any 2 of the 3 bunnies
                    should have all of the keys necessary to open the cell, but no single bunny would be able to do it.
                    Thus, the solution would be: <br/>
                    [ <br/>
                    [0, 1], <br/>
                    [0, 2], <br/>
                    [1, 2], <br/>
                    ] <br/>
                </p>
                <p>
                    <b>Test cases</b>
                </p>
                <p>
                    Input: <br/>
                    solution.solution(4, 4) <br/>
                    Output: <br/>
                    [[0], [1], [2], [3]] <br/>
                    <br/>
                    Input: <br/>
                    solution.solution(5, 3) <br/>
                    Output: <br/>
                    [[0, 1, 2, 3, 4, 5], [0, 1, 2, 6, 7, 8], [0, 3, 4, 6, 7, 9], [1, 3, 5, 6, 8, 9], [2, 4, 5, 7, 8, 9]]
                    <br/>
                    <br/>
                    Input: <br/>
                    solution.solution(2, 1) <br/>
                    Output: <br/>
                    [[0], [0]] <br/>
                </p>
            </div>

            <h2>Solution</h2>
            <p>
                I think this question was the most challenging one for me in this challenge. I spent most of my time
                trying
                to understand what the question was asking. But it can be summarized like this:
            </p>
            <blockquote>
                <p>
                    Randomly select the required bunnies from the total bunnies. Selected bunnies should have all the
                    unique
                    keys together.
                </p>
            </blockquote>

            <p>
                Say we have 5 bunnies in total and 3 required bunnies. If we select any 3 bunnies, they should have all
                the
                keys (from 0 to 9) together.
            </p>

            <div className="blog-lottie-wrapper">
                <Player
                    style={lottieStyle}
                    src={freeTheBunnyWorkers} className={"blog-lottie"}
                    autoplay
                    loop
                    background={"#e9e8e6"}>
                </Player>
            </div>

            <p>
                How do we find the number of unique keys? It is the number of combinations of the total bunnies with the
                required bunnies. In this case, it is $5 \choose 3$.
            </p>
            <p>
                I have found these patterns by trial and error:
            </p>
            <ul>
                <li><b>Total number of keys:</b> Combination(number of total bunnies, number of required bunnies) *
                    number
                    of required bunnies
                </li>
                <li><b>Number of keys each bunny has:</b> total number of keys / number of total bunnies</li>
                <li><b>Number of copies each key has:</b> number of total bunnies - number of required bunnies + 1</li>
                <li><b>Number of unique keys:</b> Combination(number of total bunnies, number of copies each key has)
                </li>
            </ul>

            <p>
                Assuming that we have 5 bunnies in total, we can get the following table using the formulas above:
            </p>
            <table>
                <thead>
                <tr>
                    <th>Buns</th>
                    <th>Req buns</th>
                    <th>Total keys</th>
                    <th>Key per bun</th>
                    <th>Copy per key</th>
                    <th>Unique key</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td>5</td>
                    <td>1</td>
                    <td>5</td>
                    <td>1</td>
                    <td>5</td>
                    <td>1</td>
                </tr>
                <tr>
                    <td>5</td>
                    <td>2</td>
                    <td>20</td>
                    <td>4</td>
                    <td>4</td>
                    <td>5</td>
                </tr>
                <tr>
                    <td>5</td>
                    <td>3</td>
                    <td>30</td>
                    <td>6</td>
                    <td>3</td>
                    <td>10</td>
                </tr>
                <tr>
                    <td>5</td>
                    <td>4</td>
                    <td>20</td>
                    <td>4</td>
                    <td>2</td>
                    <td>10</td>
                </tr>
                <tr>
                    <td>5</td>
                    <td>5</td>
                    <td>5</td>
                    <td>1</td>
                    <td>1</td>
                    <td>5</td>
                </tr>
                </tbody>
            </table>
            <p>
                But how do we assign the keys to the bunnies? After brainstorming for a long time, it clicked. We
                shouldn't
                assign keys to bunnies; we should assign bunnies to key pairs. We can find the key pairs using the
                <code>combinations</code> function from the <code>itertools</code> module.
            </p>

            <pre><code className="language-python">{`from itertools import combinations
num_buns = 5
num_required = 3

replicas = num_buns - num_required + 1

print(list(combinations(range(num_buns), replicas))

# Output
# [(0, 1, 2), (0, 1, 3), (0, 1, 4), (0, 2, 3), (0, 2, 4), (0, 3, 4), (1, 2, 3), (1, 2, 4), (1, 3, 4), (2, 3, 4)]`}</code></pre>

            <p>
                Then we can create a dictionary to store the keys for each bunny.
            </p>

            <pre><code className="language-python">{`keys = {}
            
for i in range(num_buns):
    keys[i] = []

print(keys)

# Output
# {0: [], 1: [], 2: [], 3: [], 4: []}`}</code></pre>

            <p>
                After that, we can assign the keys to the bunnies.
            </p>

            <pre><code className="language-python">{`for i in range(len(key_pairs)):
    for j in key_pairs[i]:
        print('Assigning key', i, 'to bunny', j)
        keys[j].append(i)

# Output
# Assigning key 0 to bunny 0
# Assigning key 0 to bunny 1
# Assigning key 0 to bunny 2
# Assigning key 1 to bunny 0
# Assigning key 1 to bunny 1
# Assigning key 1 to bunny 3
# Assigning key 2 to bunny 0
# Assigning key 2 to bunny 1
# Assigning key 2 to bunny 4
# Assigning key 3 to bunny 0
# Assigning key 3 to bunny 2
# Assigning key 3 to bunny 3
# Assigning key 4 to bunny 0
# Assigning key 4 to bunny 2
# Assigning key 4 to bunny 4
# Assigning key 5 to bunny 0
# Assigning key 5 to bunny 3
# Assigning key 5 to bunny 4
# Assigning key 6 to bunny 1
# Assigning key 6 to bunny 2
# Assigning key 6 to bunny 3
# Assigning key 7 to bunny 1
# Assigning key 7 to bunny 2
# Assigning key 7 to bunny 4
# Assigning key 8 to bunny 1
# Assigning key 8 to bunny 3
# Assigning key 8 to bunny 4
# Assigning key 9 to bunny 2
# Assigning key 9 to bunny 3
# Assigning key 9 to bunny 4`}</code></pre>

            <h2>Code</h2>
            <pre><code className="language-python">{`from itertools import combinations

def solution(num_buns, num_required):
    # Tricky part is to figure out how many copies of each key to give to the bunnies
    replicas = num_buns - num_required + 1

    key_pairs = list(combinations(range(num_buns), replicas))

    # Creating a dictionary to store the keys for each bunny
    keys = {}
                for i in range(num_buns):
        keys[i] = []

    # Assigning the keys to the bunnies
    for i in range(len(key_pairs)):
        for j in key_pairs[i]:
            keys[j].append(i)

    # Creating a list of the keys for each bunny
    key_list = []
    for i in range(num_buns):
        key_list.append(keys[i])

    return key_list`}</code></pre>
        </div>
    );
}